import React, { useState, useEffect } from 'react';
import { Package, Calculator, Download, FileText, AlertTriangle, CheckCircle, Truck, Box, Shield, Clock, DollarSign, Layers, ChevronRight, Info, Settings, Printer, Mail } from 'lucide-react';
import {
  ProductDimensions,
  MaterialType,
  PackagingResult,
  MATERIALS,
  calculatePackaging,
  generatePackagingPDF,
  generateShippingLabels,
  exportBoxPatternDXF
} from '../utils/packagingCalculator';

const PackagingConfiguratorPage: React.FC = () => {
  // Stan formularza
  const [dimensions, setDimensions] = useState<ProductDimensions>({
    length: 300,
    width: 200,
    height: 5,
    weight: 0.5,
    quantity: 1
  });
  
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialType>(MATERIALS[0]);
  const [result, setResult] = useState<PackagingResult | null>(null);
  const [error, setError] = useState<string>('');
  const [showLabels, setShowLabels] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [calculationHistory, setCalculationHistory] = useState<Array<{
    date: Date;
    material: string;
    dimensions: string;
    cost: number;
  }>>([]);

  // Efekt do automatycznego przeliczania
  useEffect(() => {
    calculateOptimalPackaging();
  }, [dimensions, selectedMaterial]);

  // Funkcja kalkulacji
  const calculateOptimalPackaging = () => {
    setError('');
    
    // Walidacja
    if (dimensions.length <= 0 || dimensions.width <= 0 || dimensions.height <= 0) {
      setError('Wymiary muszą być większe od zera');
      setResult(null);
      return;
    }
    
    if (dimensions.weight <= 0) {
      setError('Waga musi być większa od zera');
      setResult(null);
      return;
    }
    
    if (dimensions.quantity < 1) {
      setError('Ilość musi wynosić minimum 1');
      setResult(null);
      return;
    }
    
    // Oblicz opakowanie
    const packagingResult = calculatePackaging(dimensions, selectedMaterial);
    
    if (!packagingResult) {
      setError('Nie znaleziono odpowiedniego opakowania. Produkt jest za duży lub za ciężki.');
      setResult(null);
      return;
    }
    
    setResult(packagingResult);
    
    // Dodaj do historii
    const historyEntry = {
      date: new Date(),
      material: selectedMaterial.name,
      dimensions: `${dimensions.length}x${dimensions.width}x${dimensions.height}mm`,
      cost: packagingResult.totalCost
    };
    
    setCalculationHistory(prev => [historyEntry, ...prev.slice(0, 4)]);
  };

  // Generowanie PDF
  const handleGeneratePDF = () => {
    if (!result) return;
    
    const pdf = generatePackagingPDF(dimensions, selectedMaterial, result);
    pdf.save(`pakowanie_${new Date().getTime()}.pdf`);
  };

  // Eksport wykroju DXF
  const handleExportDXF = () => {
    if (!result) return;
    
    const dxfContent = exportBoxPatternDXF(result.box);
    const blob = new Blob([dxfContent], { type: 'application/dxf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wykroj_${result.box.id}_${new Date().getTime()}.dxf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Generowanie etykiet
  const handleGenerateLabels = () => {
    if (!result) return;
    setShowLabels(true);
  };

  // Wyślij e-mail z instrukcją
  const handleSendEmail = () => {
    if (!result) return;
    
    const subject = encodeURIComponent('Instrukcja pakowania - PlexiSystem');
    const body = encodeURIComponent(
      `Instrukcja pakowania dla:\n\n` +
      `Materiał: ${selectedMaterial.name}\n` +
      `Wymiary: ${dimensions.length}x${dimensions.width}x${dimensions.height}mm\n` +
      `Ilość: ${dimensions.quantity} szt.\n\n` +
      `Opakowanie: ${result.box.name}\n` +
      `Koszt całkowity: ${result.totalCost} PLN\n\n` +
      `Współczynnik bezpieczeństwa: ${result.safetyFactor}/10\n\n` +
      `Szczegółowa instrukcja w załączonym PDF.`
    );
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Funkcja pomocnicza do określenia koloru współczynnika bezpieczeństwa
  const getSafetyColor = (factor: number) => {
    if (factor >= 8) return 'text-green-500';
    if (factor >= 6) return 'text-yellow-500';
    return 'text-red-500';
  };

  // Funkcja pomocnicza do określenia ikony kruchości
  const getFragilityIcon = (fragility: string) => {
    switch (fragility) {
      case 'very_high': return '🥂';
      case 'high': return '⚠️';
      case 'medium': return '📦';
      default: return '✓';
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Nagłówek */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Package className="w-8 h-8" />
              Konfigurator Opakowań Transportowych
            </h1>
            <p className="text-orange-100">
              Automatyczny dobór opakowań i materiałów zabezpieczających • Redukcja uszkodzeń o 90%
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">0% uszkodzeń</div>
            <div className="text-sm text-orange-200">cel na 2025</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Panel konfiguracji */}
        <div className="lg:col-span-1 space-y-6">
          {/* Wymiary produktu */}
          <div className="bg-zinc-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Box className="w-5 h-5 text-orange-500" />
              Parametry produktu
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Długość (mm)</label>
                <input
                  type="number"
                  value={dimensions.length}
                  onChange={(e) => setDimensions({...dimensions, length: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-2 bg-zinc-700 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Szerokość (mm)</label>
                <input
                  type="number"
                  value={dimensions.width}
                  onChange={(e) => setDimensions({...dimensions, width: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-2 bg-zinc-700 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Wysokość/Grubość (mm)</label>
                <input
                  type="number"
                  value={dimensions.height}
                  onChange={(e) => setDimensions({...dimensions, height: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-2 bg-zinc-700 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  min="0.1"
                  step="0.1"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Waga jednostkowa (kg)</label>
                <input
                  type="number"
                  value={dimensions.weight}
                  onChange={(e) => setDimensions({...dimensions, weight: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-2 bg-zinc-700 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  min="0.01"
                  step="0.01"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Ilość sztuk</label>
                <input
                  type="number"
                  value={dimensions.quantity}
                  onChange={(e) => setDimensions({...dimensions, quantity: parseInt(e.target.value) || 1})}
                  className="w-full px-4 py-2 bg-zinc-700 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Wybór materiału */}
          <div className="bg-zinc-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-orange-500" />
              Materiał produktu
            </h2>
            
            <div className="space-y-2">
              {MATERIALS.map(material => (
                <button
                  key={material.id}
                  onClick={() => setSelectedMaterial(material)}
                  className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                    selectedMaterial.id === material.id
                      ? 'bg-orange-500/20 border-orange-500'
                      : 'bg-zinc-700 border-zinc-600 hover:border-zinc-500'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getFragilityIcon(material.fragility)}</span>
                    <div className="text-left">
                      <div className="font-medium">{material.name}</div>
                      <div className="text-xs text-gray-400">
                        Kruchość: {
                          material.fragility === 'very_high' ? 'Bardzo wysoka' :
                          material.fragility === 'high' ? 'Wysoka' :
                          material.fragility === 'medium' ? 'Średnia' : 'Niska'
                        }
                      </div>
                    </div>
                  </div>
                  {selectedMaterial.id === material.id && (
                    <CheckCircle className="w-5 h-5 text-orange-500" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Historia obliczeń */}
          {calculationHistory.length > 0 && (
            <div className="bg-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Historia obliczeń
              </h2>
              
              <div className="space-y-2">
                {calculationHistory.map((entry, index) => (
                  <div key={index} className="text-sm bg-zinc-700 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-gray-400">{entry.material}</div>
                        <div className="text-xs text-gray-500">{entry.dimensions}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{entry.cost} PLN</div>
                        <div className="text-xs text-gray-500">
                          {entry.date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Panel wyników */}
        <div className="lg:col-span-2 space-y-6">
          {error && (
            <div className="bg-red-900/20 border border-red-500 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-400">{error}</span>
            </div>
          )}

          {result && (
            <>
              {/* Podsumowanie */}
              <div className="bg-zinc-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold mb-6">Optymalne opakowanie</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Pudełko */}
                  <div className="bg-zinc-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium flex items-center gap-2">
                        <Package className="w-4 h-4 text-orange-500" />
                        Opakowanie
                      </h3>
                      <span className="text-2xl font-bold text-orange-500">{result.box.price} PLN</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Typ:</span>
                        <span className="font-medium">{result.box.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Wymiary wewnętrzne:</span>
                        <span>{result.box.internalLength}×{result.box.internalWidth}×{result.box.internalHeight} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Max waga:</span>
                        <span>{result.box.maxWeight} kg</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Opis:</span>
                        <span className="text-right">{result.box.description}</span>
                      </div>
                    </div>
                  </div>

                  {/* Materiały ochronne */}
                  <div className="bg-zinc-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium flex items-center gap-2">
                        <Shield className="w-4 h-4 text-orange-500" />
                        Zabezpieczenia
                      </h3>
                      <span className="text-2xl font-bold text-orange-500">
                        {result.protection.reduce((sum, p) => sum + (p.material.pricePerM2 * p.quantity), 0).toFixed(2)} PLN
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      {result.protection.map((p, index) => (
                        <div key={index}>
                          <div className="flex justify-between">
                            <span className="text-gray-400">{p.material.name}:</span>
                            <span>{p.quantity} {p.material.type === 'corner' ? 'szt.' : 'm²'}</span>
                          </div>
                          <div className="text-xs text-gray-500 ml-2">
                            {p.placement.join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Metryki */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-zinc-700 rounded-lg p-3 text-center">
                    <DollarSign className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold">{result.totalCost}</div>
                    <div className="text-xs text-gray-400">PLN całkowity koszt</div>
                  </div>
                  
                  <div className="bg-zinc-700 rounded-lg p-3 text-center">
                    <Shield className={`w-5 h-5 ${getSafetyColor(result.safetyFactor)} mx-auto mb-1`} />
                    <div className={`text-2xl font-bold ${getSafetyColor(result.safetyFactor)}`}>
                      {result.safetyFactor}/10
                    </div>
                    <div className="text-xs text-gray-400">Bezpieczeństwo</div>
                  </div>
                  
                  <div className="bg-zinc-700 rounded-lg p-3 text-center">
                    <Layers className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold">{result.stackingLimit}</div>
                    <div className="text-xs text-gray-400">Max warstw</div>
                  </div>
                  
                  <div className="bg-zinc-700 rounded-lg p-3 text-center">
                    <Clock className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold">{result.estimatedPreparationTime}</div>
                    <div className="text-xs text-gray-400">min przygotowania</div>
                  </div>
                </div>

                {/* Klasa przesyłki */}
                <div className="mt-6 bg-zinc-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Truck className="w-5 h-5 text-orange-500" />
                      <span className="font-medium">Klasa przesyłki:</span>
                    </div>
                    <span className="text-lg font-bold text-orange-500">{result.shippingClass}</span>
                  </div>
                </div>
              </div>

              {/* Instrukcje pakowania */}
              <div className="bg-zinc-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-orange-500" />
                    Instrukcja pakowania
                  </h2>
                  <button
                    onClick={() => setShowInstructions(!showInstructions)}
                    className="px-4 py-2 bg-zinc-700 rounded-lg hover:bg-zinc-600 transition-all flex items-center gap-2"
                  >
                    {showInstructions ? 'Zwiń' : 'Rozwiń'}
                    <ChevronRight className={`w-4 h-4 transition-transform ${showInstructions ? 'rotate-90' : ''}`} />
                  </button>
                </div>
                
                {showInstructions && (
                  <div className="space-y-2 text-sm bg-zinc-700 rounded-lg p-4 max-h-96 overflow-y-auto">
                    {result.instructions.map((instruction, index) => (
                      <div key={index} className={`
                        ${instruction === '' ? 'h-2' : ''}
                        ${instruction.startsWith('📦') || instruction.startsWith('🛡️') || 
                          instruction.startsWith('📐') || instruction.startsWith('🔒') || 
                          instruction.startsWith('🏷️') ? 'font-semibold text-orange-400 mt-3' : ''}
                        ${instruction.startsWith('   ') ? 'ml-6 text-gray-400' : ''}
                        ${instruction.startsWith('•') ? 'ml-4' : ''}
                      `}>
                        {instruction}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Etykiety transportowe */}
              {showLabels && (
                <div className="bg-zinc-800 rounded-xl p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-orange-500" />
                    Etykiety transportowe
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {generateShippingLabels(result, selectedMaterial).map((label, index) => (
                      <div
                        key={index}
                        className="bg-zinc-700 rounded-lg p-3 text-center font-medium border-2 border-dashed border-zinc-600"
                      >
                        <div className="text-lg">{label}</div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setShowLabels(false)}
                    className="mt-4 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Ukryj etykiety
                  </button>
                </div>
              )}

              {/* Przyciski akcji */}
              <div className="bg-zinc-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold mb-4">Akcje</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <button
                    onClick={handleGeneratePDF}
                    className="px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    PDF Instrukcja
                  </button>
                  
                  <button
                    onClick={handleExportDXF}
                    className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center justify-center gap-2"
                  >
                    <FileText className="w-5 h-5" />
                    DXF Wykrój
                  </button>
                  
                  <button
                    onClick={handleGenerateLabels}
                    className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Printer className="w-5 h-5" />
                    Etykiety
                  </button>
                  
                  <button
                    onClick={handleSendEmail}
                    className="px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Mail className="w-5 h-5" />
                    Wyślij e-mail
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Brak wyników */}
          {!result && !error && (
            <div className="bg-zinc-800 rounded-xl p-12 text-center">
              <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Brak konfiguracji</h3>
              <p className="text-gray-400">
                Wprowadź parametry produktu, aby automatycznie dobrać optymalne opakowanie
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Informacje dodatkowe */}
      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-6 border border-blue-500/30">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-400" />
          Korzyści z używania konfiguratora
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Redukcja uszkodzeń o 90%</div>
              <div className="text-gray-400">Optymalne zabezpieczenie każdego produktu</div>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Oszczędność czasu</div>
              <div className="text-gray-400">Automatyczny dobór materiałów i instrukcje</div>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Optymalizacja kosztów</div>
              <div className="text-gray-400">Wybór najtańszego skutecznego rozwiązania</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackagingConfiguratorPage;
